import { Button } from "@/components/common/Button";
import { useAuthStore } from "@/stores/authStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useSettingsStore } from "@/stores/settingsStore";

/**
 * First step — a warm greeting and a brief promise.
 *
 * The user just finished Google OAuth, so we have their name. Using it
 * here is the one moment of explicit personalization in the flow;
 * everything else stays functional.
 */
export function WelcomeStep() {
  const { user } = useAuthStore();
  const { goNext, goTo, refreshEnvironment } = useOnboardingStore();
  const cloudMode = useSettingsStore((s) => s.cloudMode);

  const firstName =
    user?.name?.split(" ")[0] ?? user?.email?.split("@")[0] ?? "there";

  const handleContinue = () => {
    if (cloudMode) {
      // Hosted cloud: the server already has the CLI + tools, so skip the
      // machine env-check / install steps and go straight to provisioning.
      goTo("provision");
      return;
    }
    // Desktop: kick off the env fetch in the background so the next screen
    // has data ready by the time the slide transition finishes.
    refreshEnvironment().catch(() => {});
    goNext();
  };

  return (
    <div className="flex flex-col items-center text-center">
      <div
        className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-jove-30 text-jove onb-scale-spring"
        aria-hidden
      >
        {/* CoWork mark — lightweight sparkle */}
        <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7">
          <path
            d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3M6.3 6.3l2.1 2.1M15.6 15.6l2.1 2.1M17.7 6.3l-2.1 2.1M8.4 15.6l-2.1 2.1"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </svg>
      </div>

      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        Hi {firstName}, let's set things up
      </h1>
      <p className="mt-3 max-w-md text-[14px] leading-relaxed text-space-50">
        {cloudMode
          ? "Nothing to set up — I'll fetch your workspace access and get to work. Takes about a minute."
          : "I'll check your machine, install anything missing, and fetch your workspace access. No keys to type. Takes about two minutes."}
      </p>

      <div className="mt-8 w-full max-w-[240px]">
        <Button variant="primary" size="lg" className="w-full" onClick={handleContinue}>
          Let's go
        </Button>
      </div>
    </div>
  );
}
